import React, { useState, useEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom'
import { supabase } from './supabaseClient'
import { PublicHomepage, TournamentYearPage, ResultsIndexPage, AustinJonesPage, AboutPage, ParticipantPage, RecordsPage, GalleryPage } from './Home'
import './App.css'

// ─── ADMIN CONFIG ────────────────────────────────────────────────────────────
const ADMIN_EMAILS = ['ron@faztoo.com']

// ─── LOGIN SCREEN ────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      setError('Invalid credentials. Contact the tournament organizer for access.')
    }
    setLoading(false)
  }

  return (
    <div className="screen login-screen">
      <div className="login-box">
        <div className="logo-mark">⚓</div>
        <h1 className="title">The Tarpon Memorial</h1>
        <p className="subtitle">Media Upload Portal</p>
        <form onSubmit={handleLogin} className="login-form">
          <div className="field">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              autoComplete="email"
            />
          </div>
          <div className="field">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
          </div>
          {error && <div className="error-msg">{error}</div>}
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <p className="access-note">Access by invitation only</p>
      </div>
    </div>
  )
}

// ─── UPLOAD SCREEN ───────────────────────────────────────────────────────────
function UploadScreen({ user, onLogout }) {
  const [step, setStep] = useState('select-year') // select-year | select-files | tag | uploading | done
  const [year, setYear] = useState(null)
  const [files, setFiles] = useState([])
  const [previews, setPreviews] = useState([])
  const [participants, setParticipants] = useState([])
  const [tagTypes, setTagTypes] = useState([])
  const [selectedParticipants, setSelectedParticipants] = useState([])
  const [selectedTags, setSelectedTags] = useState([])
  const [caption, setCaption] = useState('')
  const [progress, setProgress] = useState(0)
  const [uploadedCount, setUploadedCount] = useState(0)
  const [error, setError] = useState('')

  const YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2019, 2018, 2017, 2016, 2015, 2014, 2013, 2012, 2011, 2010, 2009]

  // Load participants for selected year + tag types
  useEffect(() => {
    if (!year) return
    const load = async () => {
      const { data: tp } = await supabase
        .from('tournament_participants')
        .select('participant_id, role, participants(id, name, role)')
        .eq('tournament_year', year)
        .order('role')

      if (tp) {
        const sorted = tp
          .map(r => r.participants)
          .sort((a, b) => {
            if (a.role !== b.role) return a.role === 'angler' ? -1 : 1
            return a.name.localeCompare(b.name)
          })
        setParticipants(sorted)
      }

      const { data: tags } = await supabase
        .from('tag_types')
        .select('*')
        .order('category')
      if (tags) setTagTypes(tags)
    }
    load()
  }, [year])

  const handleFiles = (e) => {
    const selected = Array.from(e.target.files)
    if (!selected.length) return
    const GB = 1024 * 1024 * 1024
    const oversized = selected.filter(f => f.size > GB)
    if (oversized.length) {
      setError(`${oversized.map(f => f.name).join(', ')} exceed${oversized.length === 1 ? 's' : ''} the 1GB file size limit and cannot be uploaded.`)
      return
    }
    setFiles(selected)
    const urls = selected.map(f => ({
      url: URL.createObjectURL(f),
      type: f.type.startsWith('video') ? 'video' : 'photo',
      name: f.name
    }))
    setPreviews(urls)
    setStep('tag')
  }

  const toggleParticipant = (id) => {
    setSelectedParticipants(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const toggleTag = (id) => {
    setSelectedTags(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const tagAllAnglers = () => {
    const anglerIds = participants.filter(p => p.role === 'angler').map(p => p.id)
    setSelectedParticipants(prev => [...new Set([...prev, ...anglerIds])])
  }

  const tagAllGuides = () => {
    const guideIds = participants.filter(p => p.role === 'guide').map(p => p.id)
    setSelectedParticipants(prev => [...new Set([...prev, ...guideIds])])
  }

  const tagEveryone = () => {
    setSelectedParticipants(participants.map(p => p.id))
  }

  const handleUpload = async () => {
    setStep('uploading')
    setError('')
    let count = 0

    for (const file of files) {
      try {
        const ext = file.name.split('.').pop()
        const filename = `${year}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`
        const fileType = file.type.startsWith('video') ? 'video' : 'photo'

        // Upload to Supabase Storage
        const { data: storageData, error: storageError } = await supabase.storage
          .from('tarpon-media')
          .upload(filename, file, { cacheControl: '3600', upsert: false })

        if (storageError) throw storageError

        const fileUrl = storageData.path

        // Insert media record
        const { data: mediaRecord, error: mediaError } = await supabase
          .from('media')
          .insert({
            tournament_year: year,
            file_url: fileUrl,
            file_type: fileType,
            original_filename: file.name,
            uploaded_by: user.email,
            status: 'staged',
            caption: caption || null
          })
          .select()
          .single()

        if (mediaError) throw mediaError

        const mediaId = mediaRecord.id

        // Insert participant tags
        if (selectedParticipants.length) {
          await supabase.from('media_participant_tags').insert(
            selectedParticipants.map(pid => ({ media_id: mediaId, participant_id: pid }))
          )
        }

        // Insert tag type tags
        if (selectedTags.length) {
          await supabase.from('media_tag_types').insert(
            selectedTags.map(tid => ({ media_id: mediaId, tag_type_id: tid }))
          )
        }

        count++
        setUploadedCount(count)
        setProgress(Math.round((count / files.length) * 100))
      } catch (err) {
        console.error('Upload error:', err)
        setError(`Error uploading ${file.name}: ${err.message}`)
      }
    }

    setStep('done')
  }

  const reset = () => {
    setStep('select-year')
    setYear(null)
    setFiles([])
    setPreviews([])
    setSelectedParticipants([])
    setSelectedTags([])
    setCaption('')
    setProgress(0)
    setUploadedCount(0)
    setError('')
  }

  // ── STEP: SELECT YEAR
  if (step === 'select-year') return (
    <div className="screen">
      <header className="upload-header">
        <Link to="/" className="header-title">
          <span className="anchor">⚓</span>
          <span>The Tarpon Memorial</span>
        </Link>
        <button className="btn-ghost" onClick={onLogout}>Sign out</button>
      </header>
      <div className="content">
        <h2 className="step-title">Which tournament year?</h2>
        <p className="step-sub">Select the year your photos or videos are from</p>
        <div className="year-grid">
          {YEARS.map(y => (
            <button
              key={y}
              className="year-btn"
              onClick={() => { setYear(y); setStep('select-files') }}
            >
              {y}
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  // ── STEP: SELECT FILES
  if (step === 'select-files') return (
    <div className="screen">
      <header className="upload-header">
        <button className="btn-ghost" onClick={() => setStep('select-year')}>← Back</button>
        <div className="header-year">{year}</div>
        <button className="btn-ghost" onClick={onLogout}>Sign out</button>
      </header>
      <div className="content center">
        <div className="drop-zone">
          <div className="drop-icon">📷</div>
          <h2 className="step-title">Select Photos & Videos</h2>
          <p className="step-sub">Choose multiple files at once from your camera roll · Photos and videos up to 1GB supported</p>
          <label className="btn-primary file-label">
            Choose Files
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleFiles}
              style={{ display: 'none' }}
            />
          </label>
          {error && <div className="error-msg" style={{ marginTop: '1rem' }}>{error}</div>}
        </div>
      </div>
    </div>
  )

  // ── STEP: TAG
  if (step === 'tag') {
    const anglers = participants.filter(p => p.role === 'angler')
    const guides = participants.filter(p => p.role === 'guide')
    const actionTags = tagTypes.filter(t => t.category === 'action')
    const momentTags = tagTypes.filter(t => t.category === 'moment')
    const specialTags = tagTypes.filter(t => t.category === 'special')

    return (
      <div className="screen">
        <header className="upload-header">
          <button className="btn-ghost" onClick={() => setStep('select-files')}>← Back</button>
          <div className="header-year">{year} · {files.length} file{files.length !== 1 ? 's' : ''}</div>
          <button className="btn-ghost" onClick={onLogout}>Sign out</button>
        </header>
        <div className="content">

          {/* Previews */}
          <div className="preview-strip">
            {previews.map((p, i) => (
              <div key={i} className="preview-thumb">
                {p.type === 'video'
                  ? <video src={p.url} className="thumb-img" />
                  : <img src={p.url} alt={p.name} className="thumb-img" />
                }
              </div>
            ))}
          </div>

          {/* Quick tag buttons */}
          <div className="section">
            <div className="quick-tags">
              <button className="btn-quick" onClick={tagEveryone}>+ Tag Everyone</button>
              <button className="btn-quick" onClick={tagAllAnglers}>+ All Anglers</button>
              <button className="btn-quick" onClick={tagAllGuides}>+ All Guides</button>
              <button className="btn-quick" onClick={() => setSelectedParticipants([])}>Clear</button>
            </div>
          </div>

          {/* Anglers */}
          <div className="section">
            <div className="section-label">ANGLERS</div>
            <div className="chip-grid">
              {anglers.map(p => (
                <button
                  key={p.id}
                  className={`chip ${selectedParticipants.includes(p.id) ? 'chip-active' : ''}`}
                  onClick={() => toggleParticipant(p.id)}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          {/* Guides */}
          <div className="section">
            <div className="section-label">GUIDES</div>
            <div className="chip-grid">
              {guides.map(p => (
                <button
                  key={p.id}
                  className={`chip ${selectedParticipants.includes(p.id) ? 'chip-active' : ''}`}
                  onClick={() => toggleParticipant(p.id)}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="section">
            <div className="section-label">ACTION TAGS</div>
            <div className="chip-grid">
              {actionTags.map(t => (
                <button
                  key={t.id}
                  className={`chip chip-tag ${selectedTags.includes(t.id) ? 'chip-active' : ''}`}
                  onClick={() => toggleTag(t.id)}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>

          <div className="section">
            <div className="section-label">MOMENT TAGS</div>
            <div className="chip-grid">
              {momentTags.map(t => (
                <button
                  key={t.id}
                  className={`chip chip-tag ${selectedTags.includes(t.id) ? 'chip-active' : ''}`}
                  onClick={() => toggleTag(t.id)}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>

          <div className="section">
            <div className="section-label">SPECIAL TAGS</div>
            <div className="chip-grid">
              {specialTags.map(t => (
                <button
                  key={t.id}
                  className={`chip chip-tag ${selectedTags.includes(t.id) ? 'chip-active' : ''}`}
                  onClick={() => toggleTag(t.id)}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>

          {/* Caption */}
          <div className="section">
            <div className="section-label">CAPTION (optional)</div>
            <textarea
              className="caption-input"
              placeholder="Add a caption for these photos..."
              value={caption}
              onChange={e => setCaption(e.target.value)}
              rows={3}
            />
          </div>

          {/* Submit */}
          <div className="section submit-section">
            {selectedParticipants.length > 0 && (
              <p className="tag-summary">
                {selectedParticipants.length} participant{selectedParticipants.length !== 1 ? 's' : ''} tagged
                {selectedTags.length > 0 ? ` · ${selectedTags.length} tag${selectedTags.length !== 1 ? 's' : ''}` : ''}
              </p>
            )}
            <button className="btn-primary btn-upload" onClick={handleUpload}>
              Upload {files.length} File{files.length !== 1 ? 's' : ''}
            </button>
            <p className="staged-note">Files go to staging — Ron reviews before they go live</p>
          </div>

        </div>
      </div>
    )
  }

  // ── STEP: UPLOADING
  if (step === 'uploading') return (
    <div className="screen center-all">
      <div className="upload-progress">
        <div className="progress-icon">🎣</div>
        <h2 className="step-title">Uploading...</h2>
        <p className="step-sub">{uploadedCount} of {files.length} files</p>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <p className="progress-pct">{progress}%</p>
      </div>
    </div>
  )

  // ── STEP: DONE
  if (step === 'done') return (
    <div className="screen">
      <header className="upload-header">
        <Link to="/" className="header-title">
          <span className="anchor">⚓</span>
          <span>The Tarpon Memorial</span>
        </Link>
        <button className="btn-ghost" onClick={onLogout}>Sign out</button>
      </header>
      <div className="content center-all">
        <div className="done-box">
          <div className="done-icon">🏆</div>
          <h2 className="step-title">Uploaded!</h2>
          <p className="step-sub">
            {uploadedCount} file{uploadedCount !== 1 ? 's' : ''} sent to staging.{' '}
            Ron will review and approve them.
          </p>
          {error && <div className="error-msg">{error}</div>}
          <button className="btn-primary" onClick={reset}>Upload More</button>
          <Link to="/" className="btn-ghost" style={{ display: 'block', textAlign: 'center', marginTop: '0.75rem' }}>Back to Home</Link>
        </div>
      </div>
    </div>
  )
}

// ─── MEDIA REVIEW PANEL ──────────────────────────────────────────────────────
function MediaReviewPanel({ mode }) {
  const [media, setMedia] = useState([])
  const [loadingMedia, setLoadingMedia] = useState(true)
  const [selected, setSelected] = useState(null)
  const [participants, setParticipants] = useState([])
  const [tagTypes, setTagTypes] = useState([])
  const [selParticipants, setSelParticipants] = useState([])
  const [selTags, setSelTags] = useState([])
  const [caption, setCaption] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [bulkSelected, setBulkSelected] = useState(new Set())
  const [bulkSaving, setBulkSaving] = useState(false)
  const [showCreateTag, setShowCreateTag] = useState(false)
  const [newTagName, setNewTagName] = useState('')
  const [newTagCategory, setNewTagCategory] = useState('action')
  const [creatingTag, setCreatingTag] = useState(false)
  const [tagError, setTagError] = useState('')
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lbZoom, setLbZoom] = useState(1)
  const [lbPan, setLbPan] = useState({ x: 0, y: 0 })
  const [lbDragging, setLbDragging] = useState(false)
  const lbRef = useRef(null)
  const lbZoomRef = useRef(1)
  const lbPanRef = useRef({ x: 0, y: 0 })
  const lbDragRef = useRef(null)

  useEffect(() => {
    const init = async () => {
      setLoadingMedia(true)
      const { data: rows, error: mediaErr } = await supabase
        .from('media')
        .select('*')
        .eq('status', mode)
        .order('created_at', { ascending: false })

      if (mediaErr) { setError(mediaErr.message); setLoadingMedia(false); return }
      if (!rows?.length) { setMedia([]); setLoadingMedia(false); return }

      const { data: signedUrls } = await supabase.storage
        .from('tarpon-media')
        .createSignedUrls(rows.map(m => m.file_url), 3600)

      const withUrls = rows.map((m, i) => ({
        ...m,
        signedUrl: signedUrls?.[i]?.signedUrl || null
      }))

      setMedia(withUrls)
      setSelected(withUrls[0])
      setLoadingMedia(false)
    }

    const loadTagTypes = async () => {
      const { data } = await supabase.from('tag_types').select('*').order('category')
      if (data) setTagTypes(data)
    }

    init()
    loadTagTypes()
  }, [mode])

  useEffect(() => {
    if (!selected) return
    const load = async () => {
      const { data: tp } = await supabase
        .from('tournament_participants')
        .select('participants(id, name, role)')
        .eq('tournament_year', selected.tournament_year)

      if (tp) {
        setParticipants(
          tp.map(r => r.participants).filter(Boolean).sort((a, b) => {
            if (a.role !== b.role) return a.role === 'angler' ? -1 : 1
            return a.name.localeCompare(b.name)
          })
        )
      }

      const [{ data: ptags }, { data: ttags }] = await Promise.all([
        supabase.from('media_participant_tags').select('participant_id').eq('media_id', selected.id),
        supabase.from('media_tag_types').select('tag_type_id').eq('media_id', selected.id)
      ])
      setSelParticipants(ptags?.map(t => t.participant_id) || [])
      setSelTags(ttags?.map(t => t.tag_type_id) || [])
      setCaption(selected.caption || '')
    }
    load()
  }, [selected?.id])

  const writeTagsToDb = async (mediaId) => {
    await supabase.from('media_participant_tags').delete().eq('media_id', mediaId)
    if (selParticipants.length) {
      await supabase.from('media_participant_tags').insert(
        selParticipants.map(pid => ({ media_id: mediaId, participant_id: pid }))
      )
    }
    await supabase.from('media_tag_types').delete().eq('media_id', mediaId)
    if (selTags.length) {
      await supabase.from('media_tag_types').insert(
        selTags.map(tid => ({ media_id: mediaId, tag_type_id: tid }))
      )
    }
  }

  const advanceSelection = (removedId) => {
    const idx = media.findIndex(m => m.id === removedId)
    const remaining = media.filter(m => m.id !== removedId)
    setMedia(remaining)
    setSelected(remaining[idx] || remaining[remaining.length - 1] || null)
    setSaving(false)
  }

  const handleSaveTags = async () => {
    if (!selected || saving) return
    setSaving(true)
    setError('')
    try {
      await writeTagsToDb(selected.id)
      await supabase.from('media').update({ caption: caption || null }).eq('id', selected.id)
      setMedia(prev => prev.map(m => m.id === selected.id ? { ...m, caption: caption || null } : m))
    } catch (err) { setError(err.message) }
    setSaving(false)
  }

  const handleRevoke = async () => {
    if (!selected || saving) return
    if (!window.confirm(`Revoke approval for "${selected.original_filename}"? It will return to staged.`)) return
    setSaving(true)
    setError('')
    try {
      await supabase.from('media').update({ status: 'staged' }).eq('id', selected.id)
      advanceSelection(selected.id)
    } catch (err) { setError(err.message); setSaving(false) }
  }

  const handleApprove = async () => {
    if (!selected || saving) return
    setSaving(true)
    setError('')
    try {
      await writeTagsToDb(selected.id)
      await supabase.from('media').update({ caption: caption || null, status: 'approved' }).eq('id', selected.id)
      advanceSelection(selected.id)
    } catch (err) { setError(err.message); setSaving(false) }
  }

  const handleToggleFeatured = async () => {
    if (!selected || saving) return
    setSaving(true)
    setError('')
    try {
      const newFeatured = !selected.featured
      await supabase.from('media').update({ featured: newFeatured }).eq('id', selected.id)
      const updated = { ...selected, featured: newFeatured }
      setSelected(updated)
      setMedia(prev => prev.map(m => m.id === selected.id ? updated : m))
    } catch (err) { setError(err.message) }
    setSaving(false)
  }

  const handleDelete = async () => {
    if (!selected || saving) return
    if (!window.confirm(`Delete "${selected.original_filename}"? This cannot be undone.`)) return
    setSaving(true)
    setError('')
    try {
      await supabase.storage.from('tarpon-media').remove([selected.file_url])
      await supabase.from('media').delete().eq('id', selected.id)
      advanceSelection(selected.id)
    } catch (err) { setError(err.message); setSaving(false) }
  }

  const handleBulkApprove = async () => {
    if (!bulkSelected.size || bulkSaving) return
    if (!window.confirm(`Apply current tags and approve ${bulkSelected.size} photo${bulkSelected.size !== 1 ? 's' : ''}?`)) return
    setBulkSaving(true)
    setError('')
    try {
      await Promise.all([...bulkSelected].map(async id => {
        await writeTagsToDb(id)
        await supabase.from('media').update({ caption: caption || null, status: 'approved' }).eq('id', id)
      }))
      const remaining = media.filter(m => !bulkSelected.has(m.id))
      setMedia(remaining)
      setBulkSelected(new Set())
      setSelected(remaining[0] || null)
    } catch (err) { setError(err.message) }
    setBulkSaving(false)
  }

  const handleBulkSave = async () => {
    if (!bulkSelected.size || bulkSaving) return
    setBulkSaving(true)
    setError('')
    try {
      await Promise.all([...bulkSelected].map(async id => {
        await writeTagsToDb(id)
        await supabase.from('media').update({ caption: caption || null }).eq('id', id)
      }))
      setBulkSelected(new Set())
    } catch (err) { setError(err.message) }
    setBulkSaving(false)
  }

  const handleBulkDelete = async () => {
    if (!bulkSelected.size || bulkSaving) return
    if (!window.confirm(`Delete ${bulkSelected.size} photo${bulkSelected.size !== 1 ? 's' : ''}? This cannot be undone.`)) return
    setBulkSaving(true)
    setError('')
    try {
      const toDelete = media.filter(m => bulkSelected.has(m.id))
      await Promise.all(toDelete.map(m =>
        supabase.storage.from('tarpon-media').remove([m.file_url])
      ))
      await Promise.all([...bulkSelected].map(id =>
        supabase.from('media').delete().eq('id', id)
      ))
      const remaining = media.filter(m => !bulkSelected.has(m.id))
      setMedia(remaining)
      setBulkSelected(new Set())
      setSelected(remaining[0] || null)
    } catch (err) { setError(err.message) }
    setBulkSaving(false)
  }

  const handleCreateTag = async () => {
    if (!newTagName.trim() || creatingTag) return
    setCreatingTag(true)
    setTagError('')
    try {
      const { data, error: insertErr } = await supabase
        .from('tag_types')
        .insert({ name: newTagName.trim(), category: newTagCategory })
        .select()
        .single()
      if (insertErr) throw insertErr
      setTagTypes(prev => [...prev, data].sort((a, b) => a.category.localeCompare(b.category)))
      setShowCreateTag(false)
      setNewTagName('')
      setNewTagCategory('action')
    } catch (err) { setTagError(err.message) }
    setCreatingTag(false)
  }

  const openLightbox = () => {
    setLightboxOpen(true)
    setLbZoom(1)
    setLbPan({ x: 0, y: 0 })
    lbZoomRef.current = 1
    lbPanRef.current = { x: 0, y: 0 }
    lbDragRef.current = null
  }

  const closeLightbox = () => {
    setLightboxOpen(false)
    setLbDragging(false)
    lbDragRef.current = null
  }

  useEffect(() => {
    if (!lightboxOpen) return
    const onKey = e => { if (e.key === 'Escape') closeLightbox() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightboxOpen])

  useEffect(() => {
    if (!lightboxOpen || !lbRef.current) return
    const el = lbRef.current
    const onWheel = e => {
      e.preventDefault()
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15
      const prevZoom = lbZoomRef.current
      const newZoom = Math.max(1, Math.min(8, prevZoom * factor))
      const f = newZoom / prevZoom
      const rect = el.getBoundingClientRect()
      const cx = e.clientX - (rect.left + rect.width / 2)
      const cy = e.clientY - (rect.top + rect.height / 2)
      const prev = lbPanRef.current
      const newPan = newZoom === 1
        ? { x: 0, y: 0 }
        : { x: cx * (1 - f) + prev.x * f, y: cy * (1 - f) + prev.y * f }
      lbZoomRef.current = newZoom
      lbPanRef.current = newPan
      setLbZoom(newZoom)
      setLbPan(newPan)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [lightboxOpen])

  const handleLbMouseDown = e => {
    if (e.button !== 0) return
    e.preventDefault()
    setLbDragging(true)
    lbDragRef.current = { x: e.clientX - lbPanRef.current.x, y: e.clientY - lbPanRef.current.y }
  }

  const handleLbMouseMove = e => {
    if (!lbDragRef.current) return
    const newPan = { x: e.clientX - lbDragRef.current.x, y: e.clientY - lbDragRef.current.y }
    lbPanRef.current = newPan
    setLbPan(newPan)
  }

  const handleLbMouseUp = () => {
    if (!lbDragRef.current) return
    setLbDragging(false)
    lbDragRef.current = null
  }

  const toggleParticipant = id => setSelParticipants(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const toggleTag = id => setSelTags(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const anglers = participants.filter(p => p.role === 'angler')
  const guides = participants.filter(p => p.role === 'guide')
  const actionTags = tagTypes.filter(t => t.category === 'action')
  const momentTags = tagTypes.filter(t => t.category === 'moment')
  const specialTags = tagTypes.filter(t => t.category === 'special')

  return (
    <>
      {loadingMedia ? (
        <div className="screen center-all">
          <div className="loading-anchor">⚓</div>
        </div>
      ) : media.length === 0 ? (
        <div className="screen center-all">
          <div className="admin-all-clear">
            {mode === 'staged' ? (
              <>
                <div className="admin-all-clear-icon">✓</div>
                <p>No staged media — all clear.</p>
              </>
            ) : (
              <>
                <div className="admin-all-clear-icon" style={{ fontSize: '2rem' }}>📷</div>
                <p>No approved media yet.</p>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="admin-body">

          {/* LEFT: thumbnail grid */}
          <div className={`admin-grid-panel ${bulkSelected.size > 0 ? 'bulk-mode' : ''}`}>
            <div
              className="admin-grid-header"
              onClick={() => {
                if (bulkSelected.size === media.length) setBulkSelected(new Set())
                else setBulkSelected(new Set(media.map(m => m.id)))
              }}
            >
              <div
                className={`admin-select-all-cb ${
                  bulkSelected.size === media.length && media.length > 0 ? 'cb-checked' :
                  bulkSelected.size > 0 ? 'cb-indeterminate' : ''
                }`}
              />
              <span className="admin-select-all-label">Select All</span>
            </div>
            {media.map(m => (
              <div
                key={m.id}
                className={`admin-thumb ${selected?.id === m.id ? 'admin-thumb-selected' : ''} ${bulkSelected.has(m.id) ? 'admin-thumb-bulk-selected' : ''}`}
                onClick={() => setSelected(m)}
              >
                <div
                  className={`admin-thumb-cb ${bulkSelected.has(m.id) ? 'cb-checked' : ''}`}
                  onClick={e => {
                    e.stopPropagation()
                    setBulkSelected(prev => {
                      const next = new Set(prev)
                      if (next.has(m.id)) next.delete(m.id)
                      else next.add(m.id)
                      return next
                    })
                  }}
                />
                {m.file_type === 'video'
                  ? <video src={m.signedUrl} className="admin-thumb-img" preload="metadata" muted />
                  : <img src={m.signedUrl} alt={m.original_filename} className="admin-thumb-img" />
                }
                <div className="admin-thumb-meta">
                  <span className="admin-thumb-year">{m.tournament_year}</span>
                  {m.file_type === 'video' && <span className="admin-thumb-type">VID</span>}
                </div>
                {mode === 'approved' && m.featured && (
                  <div className="admin-thumb-featured">★</div>
                )}
              </div>
            ))}
          </div>

          {/* RIGHT: tag panel */}
          <div className="admin-panel">
            {!selected ? (
              <div className="admin-empty">Select a photo to review</div>
            ) : (
              <>
                <div className="admin-preview">
                  {selected.file_type === 'video'
                    ? <video src={selected.signedUrl} controls className="admin-preview-media" />
                    : <img src={selected.signedUrl} alt={selected.original_filename} className="admin-preview-media admin-preview-zoomable" onClick={openLightbox} />
                  }
                </div>

                <div className="admin-actions">
                  <button className="btn-ghost" onClick={() => { setShowCreateTag(true); setTagError('') }}>
                    Create Tag
                  </button>
                  {bulkSelected.size > 0 && (
                    <>
                      {mode === 'staged' ? (
                        <button className="btn-approve" onClick={handleBulkApprove} disabled={bulkSaving || saving}>
                          {bulkSaving ? 'Working...' : `Approve Selected (${bulkSelected.size})`}
                        </button>
                      ) : (
                        <button className="btn-ghost" onClick={handleBulkSave} disabled={bulkSaving || saving}>
                          {bulkSaving ? 'Working...' : `Save Selected (${bulkSelected.size})`}
                        </button>
                      )}
                      <button className="btn-delete" onClick={handleBulkDelete} disabled={bulkSaving || saving}>
                        {`Delete Selected (${bulkSelected.size})`}
                      </button>
                    </>
                  )}
                  {mode === 'staged' ? (
                    <button className="btn-approve" onClick={handleApprove} disabled={saving || bulkSaving}>
                      Approve
                    </button>
                  ) : (
                    <>
                      <button className="btn-ghost" onClick={handleSaveTags} disabled={saving || bulkSaving}>
                        {saving ? 'Saving...' : 'Save Tags'}
                      </button>
                      <button
                        className={`btn-featured ${selected.featured ? 'btn-featured-on' : ''}`}
                        onClick={handleToggleFeatured}
                        disabled={saving}
                      >
                        {selected.featured ? '★ Featured' : '☆ Feature'}
                      </button>
                      <button className="btn-revoke" onClick={handleRevoke} disabled={saving || bulkSaving}>
                        Revoke Approval
                      </button>
                    </>
                  )}
                  <button className="btn-delete" onClick={handleDelete} disabled={saving || bulkSaving}>
                    Delete
                  </button>
                </div>

                <div className="admin-meta-row">
                  <span className="admin-filename">{selected.original_filename}</span>
                  <span className="admin-uploader">uploaded by {selected.uploaded_by}</span>
                </div>

                {error && <div className="error-msg">{error}</div>}

                <div className="section">
                  <div className="quick-tags">
                    <button className="btn-quick" onClick={() => setSelParticipants(participants.map(p => p.id))}>+ Everyone</button>
                    <button className="btn-quick" onClick={() => setSelParticipants(prev => [...new Set([...prev, ...anglers.map(p => p.id)])])}>+ Anglers</button>
                    <button className="btn-quick" onClick={() => setSelParticipants(prev => [...new Set([...prev, ...guides.map(p => p.id)])])}>+ Guides</button>
                    <button className="btn-quick" onClick={() => setSelParticipants([])}>Clear</button>
                  </div>
                </div>

                {anglers.length > 0 && (
                  <div className="section">
                    <div className="section-label">Anglers</div>
                    <div className="chip-grid">
                      {anglers.map(p => (
                        <button key={p.id} className={`chip ${selParticipants.includes(p.id) ? 'chip-active' : ''}`} onClick={() => toggleParticipant(p.id)}>{p.name}</button>
                      ))}
                    </div>
                  </div>
                )}

                {guides.length > 0 && (
                  <div className="section">
                    <div className="section-label">Guides</div>
                    <div className="chip-grid">
                      {guides.map(p => (
                        <button key={p.id} className={`chip ${selParticipants.includes(p.id) ? 'chip-active' : ''}`} onClick={() => toggleParticipant(p.id)}>{p.name}</button>
                      ))}
                    </div>
                  </div>
                )}

                {actionTags.length > 0 && (
                  <div className="section">
                    <div className="section-label">Action Tags</div>
                    <div className="chip-grid">
                      {actionTags.map(t => (
                        <button key={t.id} className={`chip chip-tag ${selTags.includes(t.id) ? 'chip-active' : ''}`} onClick={() => toggleTag(t.id)}>{t.name}</button>
                      ))}
                    </div>
                  </div>
                )}

                {momentTags.length > 0 && (
                  <div className="section">
                    <div className="section-label">Moment Tags</div>
                    <div className="chip-grid">
                      {momentTags.map(t => (
                        <button key={t.id} className={`chip chip-tag ${selTags.includes(t.id) ? 'chip-active' : ''}`} onClick={() => toggleTag(t.id)}>{t.name}</button>
                      ))}
                    </div>
                  </div>
                )}

                {specialTags.length > 0 && (
                  <div className="section">
                    <div className="section-label">Special Tags</div>
                    <div className="chip-grid">
                      {specialTags.map(t => (
                        <button key={t.id} className={`chip chip-tag ${selTags.includes(t.id) ? 'chip-active' : ''}`} onClick={() => toggleTag(t.id)}>{t.name}</button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="section">
                  <div className="section-label">Caption</div>
                  <textarea
                    className="caption-input"
                    placeholder="Add or edit caption..."
                    value={caption}
                    onChange={e => setCaption(e.target.value)}
                    rows={3}
                  />
                </div>

              </>
            )}
          </div>
        </div>
      )}

      {lightboxOpen && selected && selected.file_type !== 'video' && (
        <div
          className="lb-overlay"
          ref={lbRef}
          onMouseMove={handleLbMouseMove}
          onMouseUp={handleLbMouseUp}
          onMouseLeave={handleLbMouseUp}
          onClick={closeLightbox}
        >
          <button className="lb-close" onClick={e => { e.stopPropagation(); closeLightbox() }}>✕</button>
          <img
            src={selected.signedUrl}
            alt={selected.original_filename}
            className="lb-img"
            style={{
              transform: `translate(${lbPan.x}px, ${lbPan.y}px) scale(${lbZoom})`,
              cursor: lbDragging ? 'grabbing' : lbZoom > 1 ? 'grab' : 'default'
            }}
            onMouseDown={handleLbMouseDown}
            onClick={e => e.stopPropagation()}
            draggable={false}
          />
        </div>
      )}

      {showCreateTag && (
        <div className="modal-overlay" onClick={() => setShowCreateTag(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Create Tag</div>
            <div className="modal-body">
              <label className="modal-label">Tag Name</label>
              <input
                className="modal-input"
                type="text"
                placeholder="e.g. Double Hook-Up"
                value={newTagName}
                onChange={e => setNewTagName(e.target.value)}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleCreateTag()}
              />
              <label className="modal-label">Category</label>
              <div className="modal-category-group">
                {['action', 'moment', 'special'].map(cat => (
                  <div
                    key={cat}
                    className={`modal-category-option ${newTagCategory === cat ? 'modal-category-active' : ''}`}
                    onClick={() => setNewTagCategory(cat)}
                  >
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </div>
                ))}
              </div>
              {tagError && <div className="error-msg">{tagError}</div>}
            </div>
            <div className="modal-footer">
              <button className="btn-ghost" onClick={() => setShowCreateTag(false)}>Cancel</button>
              <button className="btn-approve" onClick={handleCreateTag} disabled={!newTagName.trim() || creatingTag}>
                {creatingTag ? 'Saving...' : 'Save Tag'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ─── TAG MANAGER PANEL ───────────────────────────────────────────────────────
function TagManagerPanel() {
  const [tagTypes, setTagTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [newTagName, setNewTagName] = useState('')
  const [newTagCategory, setNewTagCategory] = useState('action')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    supabase.from('tag_types').select('*').order('name').then(({ data }) => {
      if (data) setTagTypes(data)
      setLoading(false)
    })
  }, [])

  const handleAddTag = async () => {
    if (!newTagName.trim() || saving) return
    setSaving(true)
    setError('')
    try {
      const { data, error: err } = await supabase
        .from('tag_types')
        .insert({ name: newTagName.trim(), category: newTagCategory })
        .select()
        .single()
      if (err) throw err
      setTagTypes(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)))
      setNewTagName('')
    } catch (err) { setError(err.message) }
    setSaving(false)
  }

  const handleDeleteTag = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? It will be removed from all media.`)) return
    try {
      const { error: err } = await supabase.from('tag_types').delete().eq('id', id)
      if (err) throw err
      setTagTypes(prev => prev.filter(t => t.id !== id))
    } catch (err) { setError(err.message) }
  }

  const categories = ['action', 'moment', 'special']

  if (loading) return (
    <div className="screen center-all">
      <div className="loading-anchor">⚓</div>
    </div>
  )

  return (
    <div className="tag-manager-content">
      {error && <div className="error-msg tm-error">{error}</div>}

      {categories.map(cat => {
        const tags = tagTypes.filter(t => t.category === cat)
        return (
          <div key={cat} className="tm-section">
            <div className="tm-section-label">{cat.charAt(0).toUpperCase() + cat.slice(1)}</div>
            <div className="tm-tag-list">
              {tags.length === 0 ? (
                <div className="tm-empty">No {cat} tags yet.</div>
              ) : tags.map(t => (
                <div key={t.id} className="tm-tag-row">
                  <span className="tm-tag-name">{t.name}</span>
                  <button className="tm-delete-btn" onClick={() => handleDeleteTag(t.id, t.name)}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )
      })}

      <div className="tm-section tm-add-section">
        <div className="tm-section-label">Add Tag</div>
        <div className="tm-add-row">
          <input
            className="modal-input tm-add-input"
            type="text"
            placeholder="Tag name..."
            value={newTagName}
            onChange={e => setNewTagName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddTag()}
          />
          <div className="modal-category-group">
            {categories.map(cat => (
              <div
                key={cat}
                className={`modal-category-option ${newTagCategory === cat ? 'modal-category-active' : ''}`}
                onClick={() => setNewTagCategory(cat)}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </div>
            ))}
          </div>
          <button className="btn-approve" onClick={handleAddTag} disabled={!newTagName.trim() || saving}>
            {saving ? 'Adding...' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── ADMIN SCREEN ────────────────────────────────────────────────────────────
function AdminScreen({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('staged')

  return (
    <div className="admin-screen">
      <header className="upload-header">
        <div className="header-title">
          <span className="anchor">⚓</span>
          <span>Admin</span>
        </div>
        <nav className="admin-tab-nav">
          {[['staged', 'Staged'], ['approved', 'Approved'], ['tags', 'Tag Manager']].map(([id, label]) => (
            <button
              key={id}
              className={`admin-tab ${activeTab === id ? 'admin-tab-active' : ''}`}
              onClick={() => setActiveTab(id)}
            >
              {label}
            </button>
          ))}
        </nav>
        <button className="btn-ghost" onClick={onLogout}>Sign out</button>
      </header>

      <main className="admin-content">
        {activeTab === 'staged' && <MediaReviewPanel mode="staged" />}
        {activeTab === 'approved' && <MediaReviewPanel mode="approved" />}
        {activeTab === 'tags' && <TagManagerPanel />}
      </main>
    </div>
  )
}

// ─── ROOT APP ────────────────────────────────────────────────────────────────
export default function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })
    return () => subscription.unsubscribe()
  }, [])

  const handleLogout = async () => { await supabase.auth.signOut() }

  if (loading) return (
    <div className="screen center-all">
      <div className="loading-anchor">⚓</div>
    </div>
  )

  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/" element={<PublicHomepage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/austin-jones" element={<AustinJonesPage />} />
        <Route path="/results" element={<ResultsIndexPage />} />
        <Route path="/results/:year" element={<TournamentYearPage />} />
        <Route path="/participant/:id" element={<ParticipantPage />} />
        <Route path="/records" element={<RecordsPage />} />
        <Route path="/gallery" element={<GalleryPage />} />

        {/* Upload — requires login */}
        <Route path="/upload" element={
          session
            ? <UploadScreen user={session.user} onLogout={handleLogout} />
            : <LoginScreen onLogin={() => {}} />
        } />

        {/* Admin — requires admin email */}
        <Route path="/admin" element={
          !session
            ? <LoginScreen onLogin={() => {}} />
            : !ADMIN_EMAILS.includes(session.user.email)
              ? <Navigate to="/" replace />
              : <AdminScreen user={session.user} onLogout={handleLogout} />
        } />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
